import {isFunction} from 'lodash/fp';
import {useMemo} from 'react';

import {BasicTreeViewSelectionOptions, TreeViewSelection} from 'filters/features/FilterDialog/types';
import {DepartmentLevel} from 'modules/decisionsNext/api/types';

import {openFilterDialog} from 'filters';
import {useOptimizedTreeViewSelection} from 'filters/features/FilterDialog/sections/TreeViewSection/selections/useOptimizedTreeViewSelection';
import {DepartmentNode} from 'modules/decisionsNext/hooks/trees/useDepartmentTree';
import {nullIfEmpty} from 'utils/arrayUtils';

import {CounterTooltip} from 'filters/components/CounterTooltip';
import {SelectedTooltip} from 'filters/features/FilterDialog/components/SelectedTooltip';
import {DepartmentLevelBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/DepartmentLevelBlock';
import {useDepartmentLevelOptions} from 'modules/decisionsNext/components/BaseFilter/hooks/useDepartmentLevelOptions';

type DepartmentTreeViewSelectionOptions = BasicTreeViewSelectionOptions & {
  disableLevelSelection?: boolean;
};

export const useDepartmentTreeViewSelection: TreeViewSelection<
  string,
  DepartmentNode,
  {
    ids: string[] | null;
    level?: DepartmentLevel;
  },
  DepartmentTreeViewSelectionOptions
> = (tree, {value, setValue}, {disableLevelSelection = false}) => {
  const {options} = useDepartmentLevelOptions(!value.level?.length && !value.ids?.length);
  const selection = useOptimizedTreeViewSelection(
    tree,
    {
      value: value.ids,
      setValue: updated => setValue({ids: isFunction(updated) ? updated(value.ids) : updated, level: value.level}),
    },
    {
      groupSelection: true,
    }
  );

  return {
    ...selection,
    selectionCount: useMemo(() => {
      return (
        <SelectedTooltip
          title={
            selection.selected.length && !selection.isAllSelected ? (
              <CounterTooltip
                total={`Business Org selected: ${selection.selected.length}`}
                groups={options.map(option => ({
                  title: option.label,
                  count: selection.selected.filter(id => tree.treeMap.get(id)?.level === option.id).length,
                }))}
              />
            ) : null
          }
        >
          {(!selection.isAllSelected && selection.selected.length) || 'All'}
        </SelectedTooltip>
      );
    }, [options, selection.isAllSelected, selection.selected, tree.treeMap]),
    additionalFilters: !disableLevelSelection && (
      <DepartmentLevelBlock
        departmentLevelFilterStore={{
          departmentLevel: value.level,
        }}
        setDepartmentLevelFilterStore={newLevel =>
          setValue({ids: value.ids, level: newLevel.departmentLevel ?? undefined})
        }
      />
    ),
    overrideActiveFilterContent: ({sectionId}) => [
      value.level && {
        id: 'department-level',
        label: 'Business Org Level shown in charts and tables',
        chips: [
          {
            id: value.level,
            label: options.find(option => option.id === value.level)?.label || value.level,
            onClick: () => openFilterDialog(sectionId),
            onDelete: () => setValue({...value, level: undefined}),
          },
        ],
      },
      value.ids?.length && {
        id: 'business-org',
        label: 'Business Org',
        chips: value.ids.map(id => ({
          id,
          label: tree.treeMap.get(id)?.title || id,
          onClick: () => openFilterDialog(sectionId),
          onDelete: () => setValue({...value, ids: nullIfEmpty(value.ids?.filter(existingId => existingId !== id))}),
        })),
      },
    ],
  };
};
